import {
  createContext,
  Dispatch,
  forwardRef,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { RefCallBack } from 'react-hook-form'

import { AppContext } from '../AppContext/AppContext'

import Input, { InputProps } from './Input'
import MaxAmount from './MaxAmount'
import TokenSelector, { TokenSelectorProps } from './TokenSelector'
import UsdValue from './UsdValue'

export type AvailableToken = {
  balance?: string
  symbol: string
  usdPrice?: string
  availableSupply?: number
  isActive?: boolean
  address?: string
}

type CryptoInputProps = {
  initialInputValue?: string // the initial value for the input
  initialSelectedTokenSymbol?: string // the initially selected token's symbol
  availableTokens: AvailableToken[] // array of tokens to provide as option on the TokenSelector
  children?: ReactNode // OperationInput's subcomponents may be used in the children. If not provided a default layout will be rendered
  maxAmountPrefix?: string // The text to show as max amount's prefix
}

type CryptoInputContextType = {
  maxAmount?: string
  availableTokens?: AvailableToken[]
  inputValue?: string
  initialInputValue?: string
  initialSelectedTokenSymbol?: string
  maxAmountPrefix?: string
  setInputValue?: Dispatch<SetStateAction<string | number | readonly string[]>>
  setShowDisplayValue?: Dispatch<SetStateAction<boolean>>
  selectedToken?: AvailableToken
  setSelectedTokenIndex?: Dispatch<SetStateAction<number>>
  showDisplayValue?: boolean
}

export const CryptoInputContext = createContext<CryptoInputContextType>({})

function CryptoInputGroup({
  initialInputValue,
  initialSelectedTokenSymbol,
  availableTokens = [],
  children,
  maxAmountPrefix
}: CryptoInputProps) {
  const [inputValue, setInputValue] = useState<string>('')
  const [showDisplayValue, setShowDisplayValue] = useState(false)
  const [selectedTokenIndex, setSelectedTokenIndex] = useState<number>()

  const selectedToken = useMemo(
    () => availableTokens[selectedTokenIndex],
    [availableTokens, selectedTokenIndex]
  )

  return (
    <CryptoInputContext.Provider
      value={{
        availableTokens,
        initialInputValue,
        initialSelectedTokenSymbol,
        inputValue,
        maxAmountPrefix,
        selectedToken,
        setInputValue,
        setSelectedTokenIndex,
        setShowDisplayValue,
        showDisplayValue
      }}
    >
      {children}
    </CryptoInputContext.Provider>
  )
}

CryptoInputGroup.Input = forwardRef<RefCallBack, Partial<InputProps>>(
  function InputWithCryptoInputContext({ value, ...props }, ref) {
    const {
      initialInputValue,
      inputValue,
      setInputValue,
      setShowDisplayValue,
      showDisplayValue
    } = useContext(CryptoInputContext)

    // if value prop is set, it will be used to set the inputValue state.
    // This is useful when using the component with a form library that requires
    // the use of "value" prop as a regular HTML input
    useEffect(
      function () {
        if (typeof value !== 'undefined') {
          setInputValue(value)
        }
      },
      [setInputValue, value]
    )

    return (
      <Input
        {...props}
        initialValue={initialInputValue}
        ref={ref}
        setShowDisplayValue={setShowDisplayValue}
        setValue={setInputValue}
        showDisplayValue={showDisplayValue}
        value={inputValue}
      />
    )
  }
)

CryptoInputGroup.TokenSelector = function TokenSelectorWithCryptoInputContext({
  className,
  ...props
}: Partial<TokenSelectorProps>) {
  const {
    availableTokens,
    initialSelectedTokenSymbol,
    selectedToken,
    setSelectedTokenIndex
  } = useContext(CryptoInputContext)

  return (
    <TokenSelector
      {...props}
      availableTokens={availableTokens}
      className={`${
        selectedToken ? '' : '-mb-3.5 mt-3.5'
      } transition-all duration-200 ${className ? className : ''}`}
      initialSelectedTokenSymbol={initialSelectedTokenSymbol}
      selectedToken={selectedToken}
      setSelectedTokenIndex={setSelectedTokenIndex}
    />
  )
}

CryptoInputGroup.UsdValue = UsdValue
CryptoInputGroup.MaxAmount = function MaxAmountWithCryptoInputContext(props) {
  const {
    maxAmountPrefix: prefix = '',
    setInputValue,
    setShowDisplayValue,
    selectedToken
  } = useContext(CryptoInputContext)

  const {
    state: { wallet }
  } = useContext(AppContext)

  if (!selectedToken || !wallet.connected) {
    return null
  }

  return (
    <MaxAmount
      maxAmount={selectedToken?.balance}
      prefix={prefix}
      selectedToken={selectedToken}
      setInputValue={setInputValue}
      setShowDisplayValue={setShowDisplayValue}
      {...props}
    />
  )
}

export default CryptoInputGroup
